import { memo, useMemo } from 'react';
import { TooltipBody, TooltipFooter, TooltipHeader } from '@wg/wows-react-uikit';

import { t } from '~/helpers/localization';
import { ROUTES_BADGES, ROUTES_BADGES_TYPES } from '~/router/routes';

import ParticleIcon from '~/Components/ParticleIcon/ParticleIcon';

import styles from './ClanHubTile.module.scss';
import ClanHubTileCountdown from './ClanHubTileCountdown';

import type { HubTileModel } from '../clanHub.tiles.build';
import type { ROLE_NAMES } from '~/roles';
import type { BadgeModelCounter } from '~/router/navigation/types';

interface ClanHubTileTooltipProps {
  tile: HubTileModel;
  isClanMember?: boolean;
  isRewardAvailable?: boolean;
  currentRoleName?: ROLE_NAMES | null;
}

const ClanHubTileTooltip = ({
  tile,
  isClanMember = false,
  isRewardAvailable = false,
  currentRoleName = null,
}: ClanHubTileTooltipProps) => {
  const newRequestsAmount = useMemo(() => {
    return (
      tile.badgesResolved?.find(
        (badge): badge is BadgeModelCounter =>
          badge.kind === ROUTES_BADGES_TYPES.COUNTER && badge.id === ROUTES_BADGES.REQUESTS,
      )?.value || 0
    );
  }, [tile.badgesResolved]);

  return (
    <>
      <TooltipHeader isBold>{tile.title}</TooltipHeader>
      {tile.description && (
        <TooltipBody>
          {tile
            .description(currentRoleName)
            .split('\n')
            .map((line, idx, arr) => (
              <div key={`description_line_${idx}`}>
                {line}
                {idx < arr.length - 1 && <br />}
              </div>
            ))}
        </TooltipBody>
      )}
      {tile.endsAtResolved && tile.endsAtResolved > Date.now() && (
        <TooltipBody>
          <ClanHubTileCountdown
            className={styles.tileCountdownTooltip}
            endsAtResolved={tile.endsAtResolved}
            endsAtTextLabelResolved={tile.endsAtTextLabelResolved}
          />
        </TooltipBody>
      )}
      {isRewardAvailable && (
        <TooltipBody>
          <div className={styles.tileRewardMessage}>
            <ParticleIcon className={styles.particleIcon} isCompact />
            {t('Reward available')}
          </div>
        </TooltipBody>
      )}
      {!!newRequestsAmount && (
        <TooltipBody>
          {isClanMember
            ? t('New Invites: %(amount)s', { amount: newRequestsAmount })
            : t('New Requests: %(amount)s', { amount: newRequestsAmount })}
        </TooltipBody>
      )}
      {tile.warningBadgeResolved && (
        <TooltipBody>
          <span className={styles.tileWarningBadge}>{tile.warningBadgeResolved}</span>
        </TooltipBody>
      )}
      {tile.enabled && (
        <TooltipFooter mouseIcon="left">
          {tile.actionBadge ? tile.actionBadge : t('Go to %(section)s', { section: tile.title })}
        </TooltipFooter>
      )}
    </>
  );
};

export default memo(ClanHubTileTooltip);
